import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Eye, ThumbsUp, ThumbsDown, Clock } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { parseApiDate } from '../lib/utils';
import toast from 'react-hot-toast';
import { FileUploadZone, AttachmentList } from '../components/shared/Attachments';

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^\- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, match => `<ul>${match}</ul>`)
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
}

export default function KbArticlePage() {
  const { slug } = useParams();
  const { isStaff } = useAuth();
  const navigate  = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted]     = useState(null);
  const [attachRefresh, setAttachRefresh] = useState(0);

  useEffect(() => {
    api.get(`/kb/articles/${slug}`)
      .then(d => setArticle(d.article))
      .catch(() => { toast.error('Article not found'); navigate('/knowledgebase'); })
      .finally(() => setLoading(false));
  }, [slug]);

  const vote = async (type) => {
    if (voted) return;
    setVoted(type);
    await api.post(`/kb/articles/${article.id}/vote`, { vote: type });
    toast.success(type === 'yes' ? 'Thanks for your feedback!' : "Thanks, we'll improve this.");
  };

  if (loading) return (
    <div><div className="page-header"><h2>Article</h2></div>
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div></div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/knowledgebase')}><ArrowLeft size={15} /></button>
          <div className="flex items-center gap-2">
            {article.category && <span style={{ fontSize: 16 }}>{article.category.icon}</span>}
            <span className="text-muted text-sm">{article.category?.name}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <span className="flex items-center gap-1 text-muted text-sm"><Eye size={13} /> {article.views} views</span>
          {isStaff && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin?edit=${article.id}`)}>
              <Edit size={13} /> Edit
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        <div style={{ maxWidth: 740, margin: '0 auto' }}>
          <div className="card card-lg">
            {article.status !== 'published' && (
              <div style={{ background: 'var(--amber-light)', color: 'var(--amber)', border: '1px solid #fde68a', borderRadius: 'var(--radius-sm)', padding: '8px 14px', marginBottom: 20, fontSize: 13, textTransform: 'capitalize' }}>
                ⚠️ This article is <strong>{article.status}</strong> — not visible to clients.
              </div>
            )}
            <h1 style={{ fontSize: 24, letterSpacing: '-0.03em', marginBottom: 12 }}>{article.title}</h1>
            <div className="flex items-center gap-3 text-sm text-muted" style={{ marginBottom: 24 }}>
              {article.author?.full_name && <span>By {article.author.full_name}</span>}
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock size={12} />Updated {format(new Date(article.updated_at), 'MMM d, yyyy')}
              </span>
            </div>
            {(article.tags || []).length > 0 && (
              <div className="flex gap-2 mb-6" style={{ flexWrap: 'wrap' }}>
                {article.tags.map(tag => (
                  <span key={tag} style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: 12, padding: '3px 10px', borderRadius: 999, border: '1px solid var(--border)' }}>{tag}</span>
                ))}
              </div>
            )}
            <hr className="divider" />
            <div style={{ lineHeight: 1.8, fontSize: 14.5 }} className="kb-body"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(article.body) }} />
            {/* Attachments */}
            {article.id && (
              <div style={{ marginTop: 20 }}>
                <AttachmentList parentType="kb_article" parentId={article.id} refresh={attachRefresh} />
                {isStaff && (
                  <div style={{ marginTop: 12 }}>
                    <FileUploadZone parentType="kb_article" parentId={article.id} onUploaded={() => setAttachRefresh(k => k + 1)} />
                  </div>
                )}
              </div>
            )}

            <hr className="divider" />
            <div style={{ textAlign: 'center', paddingTop: 8 }}>
              <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginBottom: 12 }}>Was this article helpful?</p>
              <div className="flex gap-3" style={{ justifyContent: 'center' }}>
                <button className={`btn btn-sm ${voted === 'yes' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => vote('yes')} disabled={!!voted}>
                  <ThumbsUp size={13} /> Yes {article.helpful_yes > 0 && `(${article.helpful_yes})`}
                </button>
                <button className={`btn btn-sm ${voted === 'no' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => vote('no')} disabled={!!voted}>
                  <ThumbsDown size={13} /> No {article.helpful_no > 0 && `(${article.helpful_no})`}
                </button>
              </div>
            </div>
          </div>

          <div className="card card-sm" style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div className="font-semibold" style={{ fontSize: 13.5 }}>Still need help?</div>
              <div className="text-muted text-sm">Our support team is here for you.</div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/tickets/new')}>Submit a Ticket</button>
          </div>
        </div>
      </div>

      <style>{`
        .kb-body h1 { font-size: 22px; margin: 24px 0 12px; }
        .kb-body h2 { font-size: 18px; margin: 20px 0 10px; }
        .kb-body h3 { font-size: 15px; margin: 16px 0 8px; }
        .kb-body p  { margin-bottom: 12px; }
        .kb-body ul { padding-left: 20px; margin: 12px 0; }
        .kb-body li { margin-bottom: 6px; }
        .kb-body code { background: var(--bg-secondary); padding: 2px 6px; border-radius: 4px; font-family: var(--font-mono); font-size: 13px; }
        .kb-body a   { color: var(--blue); text-decoration: underline; }
        .kb-body hr  { margin: 20px 0; }
        .kb-body strong { font-weight: 600; }
      `}</style>
    </div>
  );
}
